<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="page-title">顧客用予定表一覧</h1>
        <div>
          <router-link :to="{ name: 'NonCreateCalendarBuildingList' }"> <!-- 未請求作業一覧へのリンク -->
            <button type="button" class="btn btn-primary mx-1">顧客予定表未作成一覧</button>
          </router-link>
          <router-link :to="{ name: 'Home' }"> <!-- 業者情報詳細ページへのリンク -->
            <button type="button" class="btn btn-primary mx-1">ホーム</button>
          </router-link>
        </div>
      </div>

      <!-- 検索バー拡張: 年月範囲検索 + 顧客 -->
      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value" >
        <form class="search-container" style="background-color: #f8f9fa;" @submit.prevent="searchBuildings"> <!-- 背景色を薄く設定 -->
          <div class="row g-3 align-items-center">
            <div class="d-flex flex-wrap align-items-center mb-2">
              <!-- 年月From -->
              <div class="d-flex align-items-center my-2 me-3">
                <label class="col-form-label me-2">予定年月</label>

                <!-- From -->
                <div class="d-flex align-items-center me-2">
                  <select class="form-select me-1" v-model="searchYearFrom" style="min-width: 120px">
                    <option value="" selected>年</option>
                    <option v-for="year in years" :key="year" :value="year">{{ year }}年</option>
                  </select>
                  <select class="form-select" v-model="searchMonthFrom" style="min-width: 100px">
                    <option value="" selected>月</option>
                    <option v-for="month in months" :key="month" :value="month">{{ month }}月</option>
                  </select>
                </div>

                <label class="col-form-label me-2">〜</label>

                <!-- To -->
                <div class="d-flex align-items-center">
                  <select class="form-select me-1" v-model="searchYearTo" style="min-width: 120px">
                    <option value="" selected>年</option>
                    <option v-for="year in years" :key="year" :value="year">{{ year }}年</option>
                  </select>
                  <select class="form-select" v-model="searchMonthTo" style="min-width: 100px">
                    <option value="" selected>月</option>
                    <option v-for="month in months" :key="month" :value="month">{{ month }}月</option>
                  </select>
                </div>
              </div>

              <!-- 顧客検索 -->
              <div class="d-flex align-items-center my-2 me-3">
                <div class="col-auto">
                  <CustomerSelectBox
                    v-model="searchCustomerName"
                    :options="customers"
                    placeholder="顧客名で検索"
                    :formatter="c => c.customer_nm"
                  />
                </div>
              </div>

              <!-- PDF出力フラグ -->
              <div class="d-flex align-items-center my-2 me-3">
                <select v-model="searchPdfOutputFlag" class="form-select" style="min-width: 150px">
                  <option value="">すべて</option>
                  <option value="true">PDF出力済み</option>
                  <option value="false">PDF未出力</option>
                </select>
              </div>

              <!-- 検索ボタン -->
              <div class="ms-2">
                <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
                <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetBuildings">クリア</button>
              </div>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="btn-cell"></th>
            <th class="text-nowrap">顧客名</th>
            <th class="text-nowrap">建物名</th>
            <th class="text-nowrap">予定年月</th>
            <th class="text-nowrap">送付先</th>
            <th class="text-nowrap">請求書出力日時</th>
            <th class="text-nowrap">作成日時</th>
            <th class="text-nowrap">更新日時</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customerCalendar in customerCalendars" :key="customerCalendar.id">
            <td  class="text-nowrap">
              <router-link
                v-if="customerCalendar"
                :to="{
                  name: 'CustomerCalendarDetail',
                  params: { id: customerCalendar.id, year: customerCalendar.work_year, month: customerCalendar.work_month },
                  query: { ...getSearchQuery(), year: customerCalendar.work_year, month: customerCalendar.work_month, from: 'list' }
                }"
              >
                <button type="button" class="btn btn-sm btn-info mx-1">予定表詳細</button>
              </router-link>

              <router-link
                :to="{
                  name: 'CustomerCalendarEdit',
                  params: { id: customerCalendar.id, year: customerCalendar.work_year, month: customerCalendar.work_month },
                  query: { ...getSearchQuery(), year: customerCalendar.work_year, month: customerCalendar.work_month, from: 'list' }
                }"
              >
                <button type="button" class="btn btn-sm btn-warning mx-2">編集</button>
              </router-link>

              <button class="btn btn-sm btn-danger" data-bs-toggle="modal" data-bs-target="#deleteModal" @click="handleDeleteClick(customerCalendar.id)">削除</button>
            </td>
            <td class="multiline-text">{{ customerCalendar.m_customer.customer_nm }}</td>
            <td class="text-nowrap">{{ customerCalendar.m_building.building_nm }}</td>
            <td class="text-nowrap">{{ customerCalendar.work_year }}年{{ customerCalendar.work_month }}月</td>
            <td class="text-nowrap">{{ customerCalendar.destination_address }}</td>
            <td class="text-nowrap text-start">{{ formatFullDateTime(customerCalendar.pdf_latest_output_datetime) }}</td>
            <td class="text-nowrap text-start">{{ formatFullDateTime(customerCalendar.created_at) }}</td>
            <td class="text-nowrap text-start">{{ formatFullDateTime(customerCalendar.updated_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 削除確認モーダル -->
    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import {
  getCustomerCalendars,
  getSelectBoxCustomers,
  deleteCustomerCalendar
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';

export default {
  components: {
    CustomerSelectBox,
    SearchPanelWrapper,
    DeleteConfirmationModal,
    Pagination
  },
  setup() {
    const title = ref('顧客予定表一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const customers = ref([]);
    const customerCalendars = ref([]);
    const years = ref([]);
    const months = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);


    // 翌月を取得
    const currentDate = new Date();
    let nextYear = currentDate.getFullYear();
    let nextMonth = currentDate.getMonth() + 2; // 現在の月に1を加算（0-indexedなので+1が必要）

    if (nextMonth > 11) { // 月が12を超える場合
      nextMonth = 1; // 翌月は1月（0-indexed）
      nextYear += 1; // 年を繰り上げ
    }

    for (let i = nextYear - 5; i <= nextYear + 5; i++) {
      years.value.push(i);
    }

    // 検索用のデータプロパティ：ここで初期値を現在の年月に設定
    const searchYearFrom = ref('');
    const searchMonthFrom = ref('');
    const searchYearTo = ref('');
    const searchMonthTo = ref('');
    const searchCustomerName = ref('');
    const searchPdfOutputFlag = ref(''); // PDF出力フラグの検索用

    // テーブルの高さの動的な設定
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    // ページネーション
    const currentPage = ref(1);
    const totalPages = ref(50); // トータルページ数を管理するためのプロパティ

    // 検索パネルの展開状態を管理するためのリアクティブプロパティ
    const isSearchExpanded = ref(true);

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => ({
      customer_calendar_year_from: searchYearFrom.value,
      customer_calendar_month_from: searchMonthFrom.value,
      customer_calendar_year_to: searchYearTo.value,
      customer_calendar_month_to: searchMonthTo.value,
      customer_calendar_customer_name: searchCustomerName.value,
      customer_calendar_pdf_output_completed: searchPdfOutputFlag.value,
      customer_calendar_page: currentPage.value || 1
    });

    const fetchBuildings = async (page = 1) => {
      try {
        const response = await getCustomerCalendars({
          year_from: searchYearFrom.value,
          month_from: searchMonthFrom.value,
          year_to: searchYearTo.value,
          month_to: searchMonthTo.value,
          customer_name: searchCustomerName.value,
          pdf_output_completed: searchPdfOutputFlag.value,
          page: page
        });
        customerCalendars.value = response.data.customer_monthly_work_calendars;
        totalPages.value = response.data.meta.total_pages;
        currentPage.value = response.data.meta.current_page;
      } catch (error) {
        console.error('顧客予定表情報ーデータの取得に失敗しました:', error);
      }

      // 顧客情報（検索用）
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }
    };

    const handleChangePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        fetchBuildings(page);

        const q = getSearchQuery();
        q.customer_calendar_page = page;

        router.replace({
          path: '/customer-calendars',
          query: q
        });

        // スクロールを tableWrapper のトップへ
        if (tableWrapper.value) {
          tableWrapper.value.scrollTop = 0;
        }
      }
    };

    // 削除モーダルへ渡すID
    const paramsIdToDelete = ref(null);

    // 削除くリック時のIDの受け渡し
    const handleDeleteClick = (customerCalendarId) => {
      paramsIdToDelete.value = customerCalendarId;
    };

    // 削除処理
    const handleConfirmDelete = async (customerCalendarId) => {
      try {
        await deleteCustomerCalendar(customerCalendarId);
        // リストから顧客予定表情報を削除
        customerCalendars.value = customerCalendars.value.filter(customerCalendar => customerCalendar.id !== customerCalendarId);
        store.dispatch('setFlashMessage', { message: '顧客予定表が削除されました', type: 'success' });
        // クエリをルートに反映
        router.push({
          path: '/customer-calendars',
          query: getSearchQuery()
        });
      } catch (error) {
        // サーバーからのエラーメッセージを取得
        const errorMessage = error.response?.data?.message || '顧客予定表削除に失敗しました';
        store.dispatch('setFlashMessage', { message: errorMessage, type: 'danger' });
      }
    };

    // 顧客予定表を検索するメソッド
    const searchBuildings = async () => {
      await fetchBuildings(1);

      const q = getSearchQuery();
      q.customer_calendar_page = 1;

      // クエリをルートに反映
      router.push({
        path: '/customer-calendars',
        query: q
      });
    };

    // 顧客予定表を検索リセットするメソッド
    const searchResetBuildings = async () => {
      // 検索条件を現在に戻す
      searchYearFrom.value = '';
      searchMonthFrom.value = '';
      searchYearTo.value = '';
      searchMonthTo.value = '';
      searchCustomerName.value = '';
      searchPdfOutputFlag.value = '';

      await fetchBuildings(1);

      const q = getSearchQuery();
      q.customer_calendar_page = 1;

      // クエリをリセット
      router.replace({
        path: '/customer-calendars',
        query: q
      });
    };

    // 年月日時分秒
    const formatFullDateTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);

      const year = date.getFullYear();
      const month = date.getMonth() + 1; // 月は0始まり
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();

      return `${year}年${month}月${day}日 ${hours}時${minutes}分${seconds}秒`;
    };

    // ▼▼▼ クエリパラメータから検索条件を復元 ▼▼▼
    onMounted(() => {
      const q = route.query;

      searchYearFrom.value = q.customer_calendar_year_from || '';
      searchMonthFrom.value = q.customer_calendar_month_from || '';
      searchYearTo.value = q.customer_calendar_year_to || '';
      searchMonthTo.value = q.customer_calendar_month_to || '';
      searchCustomerName.value = q.customer_calendar_customer_name || '';
      searchPdfOutputFlag.value = q.customer_calendar_pdf_output_completed || '';
      currentPage.value = q.customer_calendar_page ? Number(q.customer_calendar_page) : 1;

      fetchBuildings(currentPage.value);
    });

    return {
      title,
      route,
      customerCalendars,
      years,
      months,
      customers,
      paramsIdToDelete,
      handleDeleteClick,
      handleConfirmDelete,
      searchYearFrom,
      searchMonthFrom,
      searchYearTo,
      searchMonthTo,
      searchCustomerName,
      searchPdfOutputFlag,
      getSearchQuery,
      searchBuildings,
      searchResetBuildings,
      formatFullDateTime,
      topFixArea,
      tableWrapper,
      paginationContainer,
      isSearchExpanded,
      currentPage,
      totalPages,
      handleChangePage
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.building-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.building-item:hover {
  background-color: #f5f5f5;
}

table thead th {
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
}

table th,
table td{
  font-size: 14px;
}

table .btn-cell{
  width: 132px;
  min-width: 132px;
  max-width: 132px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.pagination-container button {
  margin: 0 10px;
}
</style>
